import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  DialogContentText,
  Tooltip,
  SxProps,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GridColDef } from '@mui/x-data-grid';
import CommDataGrid from '../components/CommDataGrid';
import CommonDialog from '../components/common/CommonDialog';
import { useAlert } from '../context/AlertContext';
import {
  getSummaryMsgs,
  removeSummaryMessage,
} from '../api/requests/aiSummaryMessages';
import { textSx } from './MandatoryReplies';
import {
  breadcrumbActiveStyles,
  breadcrumbIntitalStyles,
} from './mandatoryMessageDashboard';
import { BASE } from '../routes/routePaths';
import { useNavigate } from 'react-router-dom';
import { mainContentStyles } from './Dashboard';

export interface PaginationType {
  page: number;
  pageSize: number;
}

interface SummaryReportMessagesType {
  aiSummaryId: number;
  aiSummaryPrompt: string;
  aiSummaryText: string;
  id?: number;
}

interface SelectedRowType {
  id: number;
}

const AiSummaryReport: React.FC = () => {
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [summaryReportMessages, setSummaryReportMessages] = useState<
    SummaryReportMessagesType[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<SelectedRowType>();

  const fetchSummaryMessages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSummaryMsgs({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        q: searchText.trim(),
      });

      if (response && response.data && response.data.rows) {
        const mappedRows = response.data.rows.map(
          (row: SummaryReportMessagesType) => ({ ...row, id: row.aiSummaryId })
        );
        const ids = mappedRows.map((g: SummaryReportMessagesType) => g.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error('Duplicate message IDs detected:', ids);
        }
        setSummaryReportMessages(mappedRows);
        setTotalRowsCount(response.data.totalCount);
      } else {
        setSummaryReportMessages([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setSummaryReportMessages([]);
      setTotalRowsCount(0);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  const handleDelete = useCallback(async () => {
    try {
      const response =
        selectedRow && (await removeSummaryMessage(selectedRow.id));
      if (response && response.status === 200) {
        setDeleteDialogOpen(false);
        showAlert('Message deleted successfully', 'success');
        await fetchSummaryMessages();
      } else {
        showAlert('Failed to delete message', 'error');
      }
    } catch (error) {
      console.error('Error while deleting the message', error);
    }
  }, [showAlert, fetchSummaryMessages]);

  // Preserve original handleSearch function
  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  useEffect(() => {
    fetchSummaryMessages();
  }, [fetchSummaryMessages]);

  // Memoize columns for performance
  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'aiSummaryPrompt',
        headerName: 'Prompt',
        flex: 1,
        headerClassName: 'custom-header',
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography
              fontSize='14px'
              sx={{ ...textSx, pt: 2, maxWidth: '80%' }}
            >
              {value || '--'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'aiSummaryText',
        headerName: 'Text',
        flex: 1,
        headerClassName: 'custom-header',
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Tooltip title={value || ''}>
            <Typography
              fontSize='14px'
              sx={{ ...textSx, pt: 2, maxWidth: '80%' }}
            >
              {value || '--'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        headerClassName: 'custom-header',
        renderCell: ({ row }) => (
          <DeleteOutlineIcon
            sx={{ color: 'red', cursor: 'pointer', fontSize: '16px' }}
            onClick={() => {
              setSelectedRow(row);
              setDeleteDialogOpen(true);
            }}
          />
        ),
      },
    ],
    [deleteDialogOpen]
  );

  // Memoize rows
  const memoizedRows = useMemo(
    () => summaryReportMessages,
    [summaryReportMessages]
  );

  return (
    <Box sx={{ ...mainContentStyles, pb: 1 }}>
      <Box sx={titleSectionStyles}>
        <Typography onClick={() => navigate(BASE)} sx={breadcrumbIntitalStyles}>
          Dashboard
        </Typography>
        <Typography sx={breadcrumbActiveStyles}>
          / AI Summary Report Messages
        </Typography>
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          height: 'calc(100vh - 85px)',
        }}
      >
        <CommDataGrid
          title='AI Summary Report Messages'
          description='View and manage all AI summary report messages in the system'
          rows={memoizedRows}
          columns={columns}
          section='AI Summary Report Msgs'
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
          searchText={searchText}
          handleSearch={handleSearch}
          loading={loading}
        />
      </Box>
      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <CommonDialog
          title='Delete Summary Message'
          cancelButton={() => setDeleteDialogOpen(false)}
          actionButton={handleDelete}
          actionButtonTitle='Yes'
          cancelButtonTitle='Cancel'
        >
          <DialogContentText>
            <Typography
              fontSize='13px'
              sx={{ color: '#000000', paddingBottom: 1 }}
            >
              Are you sure you want to delete this summary message?
            </Typography>
            <Typography fontSize='12px'>
              {`The summary message will be deleted if you click on 'Yes'.`}
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default AiSummaryReport;

const titleSectionStyles: SxProps = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
  pt: 1,
  pl: 3,
};
